'use client';
import React, { useState, useRef, useEffect } from 'react';

interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  id?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  searchable?: boolean;
  clearable?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select one',
  disabled = false,
  required = false,
  className = '',
  label,
  error,
  id,
  name,
  size = 'md',
  variant = 'default',
  searchable = false,
  clearable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };

  const variants = {
    default: 'border border-text-muted bg-secondary focus-within:border-text-secondary-main',
    filled:
      'border-0 bg-secondary-light focus-within:bg-secondary focus-within:border-text-secondary-main',
    outlined: 'border-2 border-text-muted bg-transparent focus-within:border-text-secondary-main',
  };

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = searchable
    ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.('');
  };

  const baseClasses = `
    w-full
    rounded-lg
    font-inter
    transition-all
    duration-200
    ease-in-out
    focus-within:outline-none
    focus-within:border-text-secondary-main
    cursor-pointer
    ${value ? 'border-text-secondary-main' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed bg-bg-light' : ''}
    ${error ? 'border-red-500 focus-within:border-red-500' : ''}
  `
    .trim()
    .replace(/\s+/g, ' ');

  return (
    <div data-component-id="src/components/ui/Dropdown.tsx:110:4" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="110" data-component-end-line="219" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20relative%22%7D" className="w-full relative" ref={dropdownRef}>
      {label && (
        <label data-component-id="src/components/ui/Dropdown.tsx:112:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="112" data-component-end-line="115" data-component-file="Dropdown.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%7D" htmlFor={id} className="block text-sm font-medium text-text-primary mb-2">
          {label}
          {required && <span data-component-id="src/components/ui/Dropdown.tsx:114:23" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="114" data-component-end-line="114" data-component-file="Dropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-red-500%20ml-1%22%2C%22textContent%22%3A%22*%22%7D" className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div data-component-id="src/components/ui/Dropdown.tsx:118:6" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="118" data-component-end-line="182" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`
          ${baseClasses}
          ${sizes[size]}
          ${variants[variant]}
          ${className}
        `
          .trim()
          .replace(/\s+/g, ' ')}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div data-component-id="src/components/ui/Dropdown.tsx:129:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="129" data-component-end-line="181" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          <div data-component-id="src/components/ui/Dropdown.tsx:130:10" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="130" data-component-end-line="146" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
            {searchable && isOpen ? (
              <input data-component-id="src/components/ui/Dropdown.tsx:132:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="132" data-component-end-line="140" data-component-file="Dropdown.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchTerm%5D%22%2C%22className%22%3A%22w-full%20bg-transparent%20outline-none%20text-text-primary%22%7D"
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full bg-transparent outline-none text-text-primary"
                autoFocus
              />
            ) : (
              <span data-component-id="src/components/ui/Dropdown.tsx:142:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="142" data-component-end-line="144" data-component-file="Dropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={selectedOption ? 'text-text-primary' : 'text-text-muted'}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            )}
          </div>

          <div data-component-id="src/components/ui/Dropdown.tsx:148:10" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="148" data-component-end-line="180" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20ml-4%22%7D" className="flex items-center gap-2 ml-4">
            {clearable && value && (
              <button data-component-id="src/components/ui/Dropdown.tsx:150:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="150" data-component-end-line="164" data-component-file="Dropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-text-muted%20hover%3Atext-text-primary%20transition-colors%22%7D"
                type="button"
                onClick={handleClear}
                className="text-text-muted hover:text-text-primary transition-colors"
                aria-label="Clear selection"
              >
                <svg data-component-id="src/components/ui/Dropdown.tsx:156:16" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="156" data-component-end-line="163" data-component-file="Dropdown.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path data-component-id="src/components/ui/Dropdown.tsx:157:18" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="157" data-component-end-line="162" data-component-file="Dropdown.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            <svg data-component-id="src/components/ui/Dropdown.tsx:167:12" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="167" data-component-end-line="179" data-component-file="Dropdown.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
              className={`w-5 h-5 text-text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path data-component-id="src/components/ui/Dropdown.tsx:173:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="173" data-component-end-line="178" data-component-file="Dropdown.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div data-component-id="src/components/ui/Dropdown.tsx:186:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="186" data-component-end-line="208" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20z-50%20w-full%20mt-1%20bg-secondary%20border%20border-text-muted%20rounded-lg%20shadow-lg%20max-h-60%20overflow-y-auto%22%7D" className="absolute z-50 w-full mt-1 bg-secondary border border-text-muted rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div data-component-id="src/components/ui/Dropdown.tsx:188:12" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="188" data-component-end-line="188" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20py-3%20text-text-muted%20text-center%22%2C%22textContent%22%3A%22No%20options%20found%22%7D" className="px-4 py-3 text-text-muted text-center">No options found</div>
          ) : (
            filteredOptions.map((option) => (
              <button data-component-id="src/components/ui/Dropdown.tsx:191:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="191" data-component-end-line="205" data-component-file="Dropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                key={option.value}
                type="button"
                className={`
                  w-full px-4 py-3 text-left hover:bg-secondary-light transition-colors
                  ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  ${option.value === value ? 'bg-accent text-text-accent font-medium' : 'text-text-primary'}
                `
                  .trim()
                  .replace(/\s+/g, ' ')}
                onClick={() => !option.disabled && handleSelect(option.value)}
                disabled={option.disabled}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}

      {/* Hidden input for form submission */}
      <input data-component-id="src/components/ui/Dropdown.tsx:212:6" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="212" data-component-end-line="212" data-component-file="Dropdown.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22hidden%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%2C%22name%22%3A%22%5Bvar%3Aname%5D%22%7D" type="hidden" id={id} name={name} value={value || ''} required={required} />

      {error && (
        <p data-component-id="src/components/ui/Dropdown.tsx:215:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="215" data-component-end-line="217" data-component-file="Dropdown.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-500%22%7D" className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
